import { computed, defineComponent } from "vue";
import type { ConvertToRefs } from "@/components/composables/reactivityTypes";
import { translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";

/**
 * Mixin that will supply label and tooltip values - optionally loaded from a translation key.
 */
export default defineComponent({
  props: {
    label: { type: String, default: null, required: false },
    tooltip: { type: String, default: null, required: false },
    t: { type: [Object, String], default: undefined, required: false },
  },
  computed: {
    tooltipVal(): string {
      if (this.tooltip == null) {
        return this.t ? this.$t(`${this.t.toString()}_hint`).toString() : "";
      } else {
        return this.tooltip;
      }
    },
    labelVal(): string {
      if (this.label == null) {
        return this.t ? this.$t(this.t.toString()).toString() : "";
      } else {
        return this.label;
      }
    },
    dataQa(): string | undefined {
      // use the translation key as the data-qa attribute unless overridden
      return (
        this.$attrs["data-qa"] ||
        this.$attrs["dataQa"] ||
        this.t?.toString() ||
        this.label ||
        undefined
      );
    },
  },
});

/**
 * Note on using imported types for defineProps():
 *
 * From the docs:
 * https://vuejs.org/api/sfc-script-setup.html#defineprops-defineemits
 *
 * "In version 3.2 and below, the generic type parameter for
 * defineProps() were limited to a type literal or a reference
 * to a local interface."
 *
 * In an ideal world (i.e., not Vue 2.7), we would be able to compose
 * component prop types using interface inheritence. For example,
 * BmControlProps would extend BmLabelledProps, and the Vue compiler
 * would generate the prop declarations automatically (effectively
 * achieving what mixins can do, but in a type-safe way.)
 *
 * Unfortunately this isn't possible. We have to explicitly declare each
 * prop in a single interface, inside the file that it is used.
 *
 * So, to replicate the behaviour of using the BmLabelled mixin in script
 * setup components, we have to manually specify the BmLabelledProps
 * ourselves in each component's prop interface (see BmControl.vue for an
 * example of this).
 *
 *
 * ----
 *
 * The alternaive syntax for defining props (runtime declaration as opposed to
 * type declaration) is:
 *
 * `defineProps({name: String, t: {type: Object as PropType<Key>}});`
 * 
 * The way to circumvent the limitation of importing types is to export a common
 * prop object from BmLabelled, such as 
 * 
 * ```
import { type PropType } from "vue";
import { type ComponentObjectPropsOptions } from "vue/types/v3-component-props";
import type Key from "@/app/types/common/key";

const extraProps: ComponentObjectPropsOptions = {
  t: {
    type: Object as PropType<Key>,
  },
};
* ```

 * and then consuming components could use props like this:
```
const props = defineProps({
  ...extraProps,
  name: {
    type: String,
    required: false,
    default: "Joe",
  },
});
```
 * This works (and errors are thrown at runtime if a string is passed instead of a Key, 
 * for example), but I can't get VSCode or WebStorm to detect these props.
 * 
 * https://youtrack.jetbrains.com/issue/WEB-54775/Vue-3-runtime-declaration-of-props-factory-functions
 * 
 * This approach would be ideal if IDEs could detect these props and their types effectively,
 * however the downside of this approach is that script setup components can only use one syntax, 
 * not both.
 * 
 * "defineProps or defineEmits can only use either runtime declaration OR type declaration.
 * Using both at the same time will result in a compile error."
 */
interface BmLabelledProps {
  label?: string | null;
  tooltip?: string | null;
  t?: object | string;
  dataQa?: object | string;
}

export const useBmLabelled = (props: ConvertToRefs<BmLabelledProps>) => {
  const { label, tooltip, t } = props;

  const tooltipVal = computed((): string => {
    if (!tooltip?.value) {
      return props.t?.value
        ? translate(Key.fromString(`${props.t.value.toString()}_hint`))
        : "";
    } else {
      return tooltip.value;
    }
  });

  const labelVal = computed((): string => {
    if (!label?.value) {
      return props.t?.value
        ? translate(Key.fromString(props.t.value.toString()))
        : "";
    } else {
      return label.value;
    }
  });

  const dataQa = computed((): string | undefined => {
    // use the translation key as the data-qa attribute unless overridden
    return props.dataQa?.value?.toString() || t?.value?.toString() || undefined;
  });

  return {
    tooltipVal,
    labelVal,
    dataQa,
  };
};
